import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { MAIN_SITE_URL } from '@/mocks/categories';

const SAMPLE_ROWS = [
  { p: 'hero.item_1', c: 'cat.shoes', r: 'REF-1001', q: 'hero.qc_pending', qcPassed: false, s: 'hero.ship_standard', st: 'hero.status_considering' },
  { p: 'hero.item_2', c: 'cat.hoodies', r: 'REF-1002', q: 'hero.qc_passed', qcPassed: true, s: 'hero.ship_express', st: 'hero.status_ordered' },
  { p: 'hero.item_3', c: 'cat.jackets', r: 'REF-1003', q: 'hero.qc_pending', qcPassed: false, s: 'hero.ship_standard', st: 'hero.status_considering' },
  { p: 'hero.item_4', c: 'cat.bags', r: 'REF-1004', q: 'hero.qc_passed', qcPassed: true, s: 'hero.ship_standard', st: 'hero.status_received' },
];

export default function Hero() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  function handleSearch(e: FormEvent) {
    e.preventDefault();
    document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-background-50 to-secondary-50">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-2">
        <div className="w-full">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
            <i className="ri-leaf-line" />
            {t('hero.badge')}
          </span>

          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl lg:text-5xl">
            {t('hero.title')}
          </h1>

          <p className="mt-5 max-w-xl text-base leading-relaxed text-foreground-600 md:text-lg">
            {t('hero.subtitle')}
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-external-link-line" />
              USFans Spreadsheet
            </a>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 rounded-full bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shopping-bag-line" />
              {t('main.buy_now')}
            </a>
          </div>

          <p className="mt-5 text-xs font-medium text-foreground-500">{t('hero.trust')}</p>

          <form onSubmit={handleSearch} className="mt-6 flex w-full max-w-lg items-center gap-2 rounded-full border border-background-300/70 bg-background-50 p-1.5 pl-5">
            <i className="ri-search-line text-foreground-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('hero.search_placeholder')}
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="rounded-full bg-foreground-900 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800 whitespace-nowrap cursor-pointer"
            >
              {t('hero.cta_primary')}
            </button>
          </form>
        </div>

        <div className="w-full">
          <div className="overflow-hidden rounded-xl border border-background-300/70 bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200/70 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-accent-400" />
                <span className="h-2.5 w-2.5 rounded-full bg-secondary-400" />
                <span className="h-2.5 w-2.5 rounded-full bg-primary-400" />
              </div>
              <span className="text-xs font-medium text-foreground-500">{t('hero.table_title')}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100 text-xs text-foreground-500">
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">{t('hero.col_product')}</th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">{t('hero.col_category')}</th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">{t('hero.col_reference')}</th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">{t('hero.col_qc')}</th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">{t('hero.col_shipping')}</th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">{t('hero.col_status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {SAMPLE_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-background-100 last:border-0">
                      <td className="px-4 py-3 font-medium text-foreground-800">{t(row.p)}</td>
                      <td className="px-4 py-3 text-foreground-600">{t(row.c)}</td>
                      <td className="px-4 py-3 text-foreground-500">{row.r}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            row.qcPassed
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-secondary-100 text-secondary-700'
                          }`}
                        >
                          {t(row.q)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-600">{t(row.s)}</td>
                      <td className="px-4 py-3 text-foreground-600">{t(row.st)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}